/**
 * Generates an update operation script for CRUD tools
 */

/**
 * Generates an update operation script
 */
export function generateUpdateScript(): string {
    return String.raw`(function (inputs) {
    function getChoiceList(tableName, fieldName) {
        var choices = GlideChoiceList.getChoiceList(tableName, fieldName);
        if (!choices) return [];


        var choicesArray = [];
        for (var i = 0; i < choices.getSize(); i++) {
            choicesArray.push({
                value: choices.getChoice(i).getValue(),
                label: choices.getChoice(i).getLabel(),
            });
        }
        return choicesArray;
    }


    // parses query expression containing {{variables}}
    // by replacing {{variableName}} with the value of the variableName property in the inputs object
    // if the variableName property is not found in the inputs object, the variableName is returned as is
    function parseQuery(expression, inputs) {
        if (expression === '') return '';
        return expression.replace(
            /{{\s*(\w+)\s*}}/g,
            function (match, variableName) {
                if (inputs && inputs.hasOwnProperty(variableName)) {
                    return inputs[variableName].toLowerCase();
                }
                return match;
            }
        );
    }


    // parses {{value}} and obtains the correct value for a choice or reference field.
    function parseValue(table, value, field, inputs) {
        if (!value || typeof value === 'object') return '';
        // regex to parse {{var}} and check if the inputs contain that prop
        var fieldValue = value.replace(
            /{{\s*(\w+)\s*}}/g,
            function (match, variableName) {
                if (inputs && inputs.hasOwnProperty(variableName)) {
                    return inputs[variableName];
                }
                return '';
            }
        );
        if (!fieldValue) return field.type === 'string' ? '' : null;
        if (field.type === 'choice') {
            var choices = getChoiceList(table, field.id);
            for (var i = 0; i < choices.length; i++) {
                if (
                    choices[i].value == fieldValue ||
                    choices[i].label
                        .toLowerCase()
                        .includes(fieldValue.toLowerCase())
                ) {
                    return choices[i].value;
                }
            }
            // Neither value nor label matched
            gs.error(
                '[AIA AGENTS MSG] Invalid choice field value or label: {0}',
                fieldValue
            );
        } else if (field.referenceTable) {
            // First check if the input is a valid sys_id
            var refGr = new GlideRecordSecure(field.referenceTable);
            refGr.addQuery('sys_id', fieldValue);
            refGr.query();
            if (refGr.getRowCount() === 1) {
                return fieldValue; // User input is a valid sys_id
            }


            if (field.referenceTableDefaultField) {
                // If not, try to find a record with matching display value
                var refName = field.referenceTableDefaultField;
                refGr = new GlideRecordSecure(field.referenceTable);


                // Check for both exact match and display value containing the input
                var qr = refGr.addQuery(refName, fieldValue);
                qr.addOrCondition(refName, 'CONTAINS', fieldValue);
                refGr.setLimit(1);
                refGr.query();


                if (refGr.next()) {
                    return refGr.getUniqueValue(); // Return the sys_id
                }
            }
            // No match found
            gs.error(
                '[AIA AGENTS MSG] Invalid reference field value or label: {0}',
                fieldValue
            );
        }
        return fieldValue;
    }


    var table = inputs.crudInputs.table.value;
    var tableName = inputs.crudInputs.table.displayValue;
    var query = parseQuery(inputs.crudInputs.query, inputs);
    var fieldValues = inputs.crudInputs.fieldValues
        ? inputs.crudInputs.fieldValues
        : [];
    fieldValues = fieldValues.map(function (item) {
        var fieldValue = item.value ? item.value.fieldValue : '';
        return {
            field: item.field.id,
            value: parseValue(table, fieldValue, item.field, inputs),
        };
    });


    var gr = new GlideRecordSecure(table);
    if (query) {
        gr.addEncodedQuery(query, true);
    } else {
        gs.error('[AIA error] No query provided');
        return {
            message: 'No query provided',
            status: sn_i18n.Message.getMessage('sn_aia', 'error'),
        };
    }


    var updated = [];
    var failed = [];


    gr.query();
    while (gr.next()) {
        if (gr.canWrite()) {
            // Store original record ID
            var recordId = gr.getUniqueValue();


            // Set field values
            fieldValues.forEach(function (item) {
                if (gr.isValidField(item.field)) {
                    gr.setValue(item.field, item.value);
                }
            });


            // Attempt update
            var updatedId = gr.update();


            if (updatedId) {
                updated.push(updatedId);
            } else {
                // Update operation failed entirely
                failed.push(recordId);
            }
        }
    }


    var fullSuccessCount = updated.length;
    var completeFailCount = failed.length;


    var totalCount = fullSuccessCount + completeFailCount;


    // Create simple main message: X out of Y records updated
    var mainMessage;
    if (totalCount === 1) {
        // Single record case
        if (fullSuccessCount === 1) {
            mainMessage = sn_i18n.Message.getMessage(
                'sn_aia',
                '1 out of 1 record updated in the {table} table.',
                {
                    table: tableName,
                }
            );
        } else {
            mainMessage = sn_i18n.Message.getMessage(
                'sn_aia',
                '0 out of 1 record updated in the {table} table.',
                {
                    table: tableName,
                }
            );
        }
    } else {
        // Multiple records case
        mainMessage = sn_i18n.Message.getMessage(
            'sn_aia',
            '{successCount} out of {totalCount} records updated in the {table} table.',
            {
                successCount: fullSuccessCount.toString(),
                totalCount: totalCount.toString(),
                table: tableName,
            }
        );
    }


    // Create detailed messages for different update types
    var successMessage = '';
    var failureMessage = '';


    // Success message
    if (fullSuccessCount > 0) {
        var fullSuccessSingular = sn_i18n.Message.getMessage(
            'sn_aia',
            '{count} record completely updated: {ids}. ',
            {
                count: fullSuccessCount.toString(),
                ids: JSON.stringify(updated),
            }
        );
        var fullSuccessPlural = sn_i18n.Message.getMessage(
            'sn_aia',
            '{count} records completely updated: {ids}. ',
            {
                count: fullSuccessCount.toString(),
                ids: JSON.stringify(updated),
            }
        );


        successMessage = fullSuccessCount === 1 ? fullSuccessSingular : fullSuccessPlural;
    }


    // Complete failure message
    if (completeFailCount > 0) {
        var failureSingular = sn_i18n.Message.getMessage(
            'sn_aia',
            '{count} record failed to update: {ids}. ',
            {
                count: completeFailCount.toString(),
                ids: JSON.stringify(failed),
            }
        );
        var failurePlural = sn_i18n.Message.getMessage(
            'sn_aia',
            '{count} records failed to update: {ids}. ',
            {
                count: completeFailCount.toString(),
                ids: JSON.stringify(failed),
            }
        );


        failureMessage = completeFailCount === 1 ? failureSingular : failurePlural;
    }


    // Build detailed message
    var detailedMessage = '';
    if (successMessage) detailedMessage += successMessage;
    if (failureMessage) detailedMessage += failureMessage;


    var status;
    if (totalCount === 0) {
        status = sn_i18n.Message.getMessage('sn_aia', 'success'); // No records to update
    } else if (failed.length === 0) {
        status = sn_i18n.Message.getMessage('sn_aia', 'success'); // All records fully updated successfully
    } else {
        status = sn_i18n.Message.getMessage('sn_aia', 'error'); // Some records failed to update
    }


    return {
        message: mainMessage,
        detailedMessage: detailedMessage,
        status: status,
    };
})(inputs);`
}
